import type { CreateManualTaskDto } from '../../../../../../services/taskQueueService';
import type { TaskDepartment } from '../../../../../../constants/taskDepartments';
import type { VacationSupplyFormRequest } from '../../../../../../services/patientDocumentsService';
import {
  COVERAGE_LABELS,
  type Coverage,
  type VacationPlan,
  type VacationRowResult,
  type VacationTripInput,
} from './vacationRules';

/**
 * PRX-130 — "Create vacation supply request" is a manual task (the PRX-67
 * task system) carrying the computed plan. Nothing is billed and nothing is
 * written to the pharmacy system; how the request becomes a fill is for the
 * pharmacy to define.
 *
 * WP-10 (Q15): at a Fillware pharmacy there is no task. The same plan becomes
 * a form for the patient's signature (`buildVacationFormRequest`), saved to
 * the patient's documents and tied to its Rx numbers. The server prints the
 * patient's name from its own record.
 */
type OkPlan = Extract<VacationPlan, { status: 'ok' }>;

export interface VacationCoverageChoice {
  value: Coverage;
  /** The default's source (or why there was none). */
  source: string;
  /** False when staff changed the coverage from the default. */
  isDefault: boolean;
}

const plural = (n: number, word: string) => `${n} ${n === 1 ? word : `${word}s`}`;
const yesNo = (b: boolean) => (b ? 'yes' : 'no');

function rowLine(r: VacationRowResult): string {
  const name = r.drugName || 'no drug name';
  const parts: string[] = [];
  if (r.bridgeQty > 0) parts.push(`bridge ${r.bridgeQty} (${plural(r.bridgeDays, 'day')}) by ${r.runsOut}`);
  if (r.fillQty > 0) parts.push(`fill ${r.fillQty} (${plural(r.fillDays, 'day')})`);
  if (r.status === 'enough_on_hand') parts.push('enough on hand, no fill');
  let line = `- Rx ${r.rxNumber} ${name}: ${parts.join(', ')}`;
  line += `; per day ${r.perDay != null ? Number(r.perDay.toFixed(2)) : '?'}, on hand ${r.onHandQty ?? '?'}, runs out ${r.runsOut ?? '?'}`;
  if (r.shortDays > 0) line += `; short by ${plural(r.shortDays, 'day')}`;
  if (r.ruleDescription) line += `; rule ${r.ruleDescription}`;
  return line;
}

export function buildVacationTaskBody(plan: OkPlan, trip: VacationTripInput, coverage: VacationCoverageChoice): string {
  const coverageLine = coverage.isDefault
    ? `Coverage: ${COVERAGE_LABELS[coverage.value]} (default from file: ${coverage.source})`
    : `Coverage: ${COVERAGE_LABELS[coverage.value]} (chosen by staff; default from file: ${coverage.source})`;
  const selected = plan.rows.filter((r) => r.selected);
  const lines = [
    `Vacation supply request. Rules version ${plan.rulesVersion}.`,
    `Trip: ${trip.departureDate} to ${trip.returnDate}, buffer ${plural(trip.bufferDays as number, 'day')}, ${trip.destination === 'outside_ontario' ? 'outside Ontario' : 'in Ontario'}`,
    coverageLine,
    plan.rules.length > 0 ? `Rules: ${plan.rules.join('; ')}` : null,
    `Max single fill: ${trip.maxSingleFillDays != null ? plural(trip.maxSingleFillDays, 'day') : 'none'}`,
    `On file: travel letter or insurance ${yesNo(trip.travelLetterOnFile)}; plan override ${yesNo(trip.overrideOnFile)}; ODB vacation supply used this benefit year ${yesNo(trip.odbVacationUsed)}`,
    `Recommended fill date: ${plan.recommendedFillDate ?? 'no fill needed'}`,
    `Bridge fill: ${plan.bridgeNeeded ? 'needed' : 'not needed'}`,
    'Medications:',
    ...selected.map(rowLine),
    ...(plan.warnings.length > 0 ? ['Warnings:', ...plan.warnings.map((w) => `- ${w.message}`)] : []),
    'Nothing was billed and nothing was written to the pharmacy system.',
  ];
  return lines.filter((l): l is string => l != null).join('\n');
}

export function buildVacationTask(input: {
  plan: OkPlan;
  trip: VacationTripInput;
  coverage: VacationCoverageChoice;
  patientId: string;
  patientName: string | null;
  department: TaskDepartment;
  assignedTo: string | null;
}): CreateManualTaskDto {
  const { plan, trip, coverage, patientId, patientName, department, assignedTo } = input;
  return {
    title: `Vacation supply request: ${trip.departureDate} to ${trip.returnDate}`,
    description: buildVacationTaskBody(plan, trip, coverage),
    patientId,
    ...(patientName ? { patientName } : {}),
    department,
    ...(assignedTo ? { assignedTo } : {}),
    ...(plan.recommendedFillDate ? { dueDate: plan.recommendedFillDate } : {}),
    source: 'patient360_vacation_supply',
    category: 'vacation_supply',
    tags: ['vacation_supply'],
  };
}

/** Whether "Create vacation supply request" may be used, and why not. */
export function canRequest(
  plan: VacationPlan,
  readOnly: boolean,
  readOnlyMessage?: string | null,
): { ok: true } | { ok: false; reason: string } {
  if (readOnly) return { ok: false, reason: readOnlyMessage || 'This chart is read-only' };
  if (plan.status !== 'ok') return { ok: false, reason: plan.reasons[0] };
  const selected = plan.rows.filter((r) => r.selected);
  if (selected.some((r) => r.status === 'cannot_calculate')) {
    return { ok: false, reason: "A ticked medication can't be calculated" };
  }
  if (!selected.some((r) => r.fillQty > 0 || r.bridgeQty > 0)) return { ok: false, reason: 'Nothing ticked needs a fill' };
  const codes = plan.warnings.map((w) => w.code);
  if (codes.includes('fill_after_departure')) {
    return { ok: false, reason: 'Get a plan override, then tick Plan override on file' };
  }
  if (codes.includes('odb_second_early_fill')) return { ok: false, reason: 'A second early fill will reject' };
  return { ok: true };
}

const needsSupply = (r: VacationRowResult) => r.selected && (r.fillQty > 0 || r.bridgeQty > 0);

/** WP-10: what the vacation supply form prints, one line per ticked Rx that needs a fill or a bridge. */
export function buildVacationFormRequest(input: { plan: OkPlan; trip: VacationTripInput }): VacationSupplyFormRequest {
  const { plan, trip } = input;
  return {
    departureDate: trip.departureDate as string,
    returnDate: trip.returnDate as string,
    destination: trip.destination as NonNullable<VacationTripInput['destination']>,
    bufferDays: trip.bufferDays as number,
    ...(plan.recommendedFillDate ? { fillDate: plan.recommendedFillDate } : {}),
    items: plan.rows.filter(needsSupply).map((r) => ({
      rxNumber: r.rxNumber,
      drugName: r.drugName,
      fillQty: r.fillQty,
      fillDays: r.fillDays,
      bridgeQty: r.bridgeQty,
      bridgeDays: r.bridgeDays,
    })),
  };
}

/** WP-10: whether the form can be printed. A missing drug name is refused, never replaced. */
export function formGate(plan: OkPlan): { ok: true } | { ok: false; reason: string } {
  const unnamed = plan.rows.filter(needsSupply).find((r) => !r.drugName.trim());
  if (unnamed) return { ok: false, reason: `Rx ${unnamed.rxNumber} has no drug name` };
  return { ok: true };
}
